import type { JSX } from "preact";
import { useState } from "preact/hooks";
import type { Path } from "slate";
import { Editor, Point, Range, Transforms } from "slate";
import { useSlate } from "slate-react";
import { Col } from "@gss/components/Col";
import { ConfirmModal } from "@gss/components/ConfirmModal";
import { FormLabel } from "@gss/components/FormLabel";
import { InputNumber } from "@gss/components/InputNumber";
import { Row } from "@gss/components/Row";
import { Table } from "../components/icons";
import { Popover } from "../components/Popover";
import type { TableElement, TableRowElement } from "./types";
import { getNode, isActive, removeNodes } from "./util";

export function AddTableButton(): JSX.Element {
    const editor = useSlate();

    const onSave = (dimensions: Dimensions) => {
        const table = getTableElement(editor);
        const isValid = dimensions.cols > 0 && dimensions.rows > 0;
        if (table != null) {
            // Resize existing table
            if (isValid) {
                resizeTable(editor, table, dimensions);
            }

            // Remove existing table
            else {
                removeTable(editor);
            }
        }

        // Insert new table
        else if (isValid) {
            insertTable(editor, dimensions);
        }
    };

    return (
        <Popover
            overlay={(hide) => (
                <Overlay
                    resolve={(dimensions) => {
                        onSave(dimensions);
                        hide();
                    }}
                    reject={hide}
                    table={getTableElement(editor)}
                />
            )}
        >
            <button
                type="button"
                className={isTableActive(editor) ? "active" : undefined}
            >
                <Table />
            </button>
        </Popover>
    );
}

interface OverlayParameters {
    resolve: (dimensions: Dimensions) => void;
    reject: () => void;
    table?: TableElement;
}

function Overlay({ resolve, reject, table }: OverlayParameters) {
    const [rows, setRows] = useState<number | undefined>(
        table?.children.length ?? 1,
    );
    const [cols, setCols] = useState<number | undefined>(
        table?.children[0]?.children.length ?? 1,
    );

    return (
        <ConfirmModal
            disabled={cols == null || rows == null}
            resolve={() => {
                if (rows != null && cols != null) {
                    resolve({ rows, cols });
                }
            }}
            reject={reject}
        >
            <Row className="text-editor-popover-table">
                {renderInput("Rader", rows, setRows)}
                {renderInput("Kolumner", cols, setCols)}
            </Row>
        </ConfirmModal>
    );
}

function renderInput(
    title: string,
    value: number | undefined,
    setValue: (value: number | undefined) => void,
): JSX.Element {
    return (
        <Col>
            <FormLabel>{title}</FormLabel>
            <InputNumber
                small
                isInteger
                invalid={value == null}
                value={value}
                onChange={setValue}
            />
        </Col>
    );
}

interface Dimensions {
    rows: number;
    cols: number;
}

function isTableActive(editor: Editor): boolean {
    return isActive(editor, "table");
}

function getTableElement(editor: Editor): TableElement | undefined {
    const node = getNode(editor, "table");
    return node?.[0];
}

function insertTable(editor: Editor, dimensions: Dimensions) {
    const table = createTable([], dimensions);
    Transforms.insertNodes(editor, table);
}

function removeTable(editor: Editor) {
    removeNodes(editor, "table");
}

function resizeTable(
    editor: Editor,
    oldTable: TableElement,
    dimensions: Dimensions,
) {
    const newTable = createTable(oldTable.children, dimensions);
    removeTable(editor);
    Transforms.insertNodes(editor, newTable);
}

function createTable(
    tableChildren: TableRowElement[],
    dimensions: Dimensions,
): TableElement {
    const table: TableElement = { type: "table", children: [] };
    for (let i = 0; i < dimensions.rows; ++i) {
        const row: TableRowElement = { type: "table-row", children: [] };
        for (let j = 0; j < dimensions.cols; ++j) {
            const children = tableChildren[i]?.children[j]?.children ?? [
                { text: "" },
            ];
            row.children.push({ type: "table-cell", children });
        }
        table.children.push(row);
    }
    return table;
}

export function withTables(editor: Editor): Editor {
    const { deleteBackward, deleteForward, insertBreak } = editor;

    const getCellPath = (): Path | undefined => {
        const { selection } = editor;
        if (selection && Range.isCollapsed(selection)) {
            const cell = getNode(editor, "table-cell");
            if (cell) {
                const [, cellPath] = cell;
                return cellPath;
            }
        }
        return undefined;
    };

    // Don't allow deletion backwards at start of table cell
    editor.deleteBackward = (unit) => {
        const cellPath = getCellPath();
        if (cellPath) {
            const { selection } = editor;
            const start = Editor.start(editor, cellPath);
            if (selection && Point.equals(selection.anchor, start)) {
                return;
            }
        }
        deleteBackward(unit);
    };

    // Don't allow deletion forwards at end of table cell
    editor.deleteForward = (unit) => {
        const cellPath = getCellPath();
        if (cellPath) {
            const { selection } = editor;
            const end = Editor.end(editor, cellPath);
            if (selection && Point.equals(selection.anchor, end)) {
                return;
            }
        }
        deleteForward(unit);
    };

    // Don't allow new lines in table
    editor.insertBreak = () => {
        if (editor.selection && isTableActive(editor)) {
            return;
        }
        insertBreak();
    };

    return editor;
}
